import {
  div,
  h1,
  button,
  span,
  label,
  input,
  option,
  select,
  figure,
  img,
  fnstate,
  goTo
} from '@srfnstack/fntags'
import { createPaginatedList } from '../utils/pagination.js'
import { lastJobUpdate } from '../state.js'

const PROMOTED_OPTIONS = [
  { value: '', label: 'All Artwork' },
  { value: 'promoted', label: 'Has Promoted' },
  { value: 'unromoted', label: 'Not Promoted' }
]

export const ArtworkReview = () => {
  const showRejected = fnstate(false)
  const promotedFilter = fnstate('')
  const search = fnstate('')
  let searchTimer = null

  const SCROLL_KEY = 'artworkReview_scrollY'

  const buildUrl = (offset, limit) => {
    const params = new URLSearchParams({ limit, offset, _: Date.now() })
    if (showRejected()) params.set('show_rejected', 'true')
    if (promotedFilter()) params.set('promoted_filter', promotedFilter())
    const q = search().trim()
    if (q) params.set('search', q)
    return `/api/prompts?${params}`
  }

  // The request-token guard that stops a previous filter's page landing in the
  // current list lives in createPaginatedList, so this page cannot omit it.
  const list = createPaginatedList({ buildUrl, limit: 20 })
  const { items: prompts, total, loading, loadingMore, sentinel } = list
  const fetchPrompts = list.reload

  let scrollRestored = false
  prompts.subscribe(() => {
    if (scrollRestored) return
    scrollRestored = true
    const saved = sessionStorage.getItem(SCROLL_KEY)
    if (saved) {
      sessionStorage.removeItem(SCROLL_KEY)
      requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10)))
    }
  })

  showRejected.subscribe(fetchPrompts)
  promotedFilter.subscribe(fetchPrompts)

  const handleJobUpdate = (event) => {
    if (event && (event.job_type === 'IMAGE_GENERATION' || event.job_type === 'PROMPT_ENHANCE')) {
      fetchPrompts()
    }
  }

  lastJobUpdate.subscribe(handleJobUpdate)

  const handleImport = async (file) => {
    if (!file) return
    const formData = new FormData()
    formData.append('image', file)

    const res = await fetch('/api/ingest/artwork', {
      method: 'POST',
      body: formData
    })

    if (res.ok) {
      await window.alert('Artwork imported successfully!')
      fetchPrompts()
    } else {
      await window.alert('Failed to import artwork.')
    }
  }

  fetchPrompts()

  return div({ class: 'p-4 md:p-6 flex flex-col min-h-[calc(100vh-64px)]' },
    div({ class: 'flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6' },
      div({ class: 'flex items-center gap-3' },
        h1({ class: 'text-2xl md:text-3xl font-bold' }, 'Artwork Manager'),
        total.bindAs(t => t > 0 ? span({ class: 'badge badge-ghost font-mono' }, t) : span())
      ),
      div({ class: 'flex flex-wrap items-center gap-2 md:gap-4 w-full md:w-auto' },
        input({
          type: 'search',
          class: 'input input-bordered input-sm w-full md:w-64',
          placeholder: 'Search title or prompt…',
          value: search(),
          oninput: (e) => {
            search(e.target.value)
            clearTimeout(searchTimer)
            searchTimer = setTimeout(fetchPrompts, 300)
          }
        }),
        select({
          class: 'select select-bordered select-sm',
          onchange: (e) => promotedFilter(e.target.value)
        },
        PROMOTED_OPTIONS.map(o => option({ value: o.value, selected: promotedFilter() === o.value }, o.label))
        ),
        label({ class: 'flex items-center gap-2 cursor-pointer' },
          span({ class: 'text-sm font-medium opacity-60' }, 'Show Rejected'),
          input({
            type: 'checkbox',
            class: 'toggle toggle-sm',
            checked: showRejected.bindAttr(),
            onchange: (e) => showRejected(e.target.checked)
          })
        ),
        label({ class: 'btn btn-primary btn-sm flex-grow md:flex-grow-0' },
          input({
            type: 'file',
            accept: 'image/*',
            class: 'hidden',
            onchange: (e) => handleImport(e.target.files[0])
          }),
          'Import Image'
        ),
        button({ class: 'btn btn-outline btn-sm flex-grow md:flex-grow-0', onclick: fetchPrompts }, 'Refresh')
      )
    ),
    loading.bindAs(l => l
      ? div({ class: 'flex items-center justify-center h-64' }, span({ class: 'loading loading-spinner loading-lg text-primary' }))
      : prompts.bindAs(list => {
        if (!list || list.length === 0) {
          return div({ class: 'bg-base-100 rounded-xl border border-base-300 p-12 text-center opacity-60 flex flex-col items-center gap-2 shadow-sm' },
            span({ class: 'text-4xl' }, '🎨'),
            span('No prompts ready for review.')
          )
        }
        return div({ class: 'flex flex-col gap-6' },
          div({ class: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' },
            list.map(p => div({
              class: `card bg-base-100 shadow-md border ${p.status === 'REJECTED' ? 'border-error/40 opacity-60' : 'border-base-300'} hover:border-primary transition-all cursor-pointer group flex flex-col`,
              onclick: () => { sessionStorage.setItem(SCROLL_KEY, window.scrollY); goTo(`/artwork-review/${p.id}`) }
            },
            p.preview_image
              ? figure({ class: 'aspect-square overflow-hidden border-b border-base-300' },
                img({
                  src: `/api/image?file=${encodeURIComponent(p.preview_image)}`,
                  class: `object-cover w-full h-full transition-transform group-hover:scale-105${p.status === 'REJECTED' ? ' grayscale' : ''}`
                })
              )
              : div({ class: 'aspect-square bg-base-200 flex items-center justify-center border-b border-base-300' },
                span({ class: 'text-4xl opacity-20' }, '🖼️')
              ),
            div({ class: 'card-body p-5 flex flex-col gap-3' },

              div({ class: 'flex justify-between items-start gap-4' },
                div({ class: 'flex flex-col gap-1' },
                  div({ class: 'flex flex-wrap items-center gap-1.5' },
                    div({ class: 'badge badge-primary font-bold h-auto py-1 text-xs' }, p.display_title || p.product_name || 'Manual'),
                    p.has_promoted ? span({ class: 'badge badge-success badge-xs font-bold' }, 'Promoted') : '',
                    p.status === 'REJECTED' ? span({ class: 'badge badge-error badge-xs font-bold' }, 'Rejected') : ''
                  ),
                  span({ class: 'text-[10px] font-bold opacity-40' }, `#${p.id}`)
                ),
                span({ class: 'text-[10px] uppercase tracking-wider opacity-40 font-bold shrink-0 mt-1' },
                  new Date(p.created_at).toLocaleDateString()
                )
              ),

              div({ class: 'flex-grow overflow-hidden' },
                div({ class: 'text-sm leading-relaxed line-clamp-4 group-hover:text-primary transition-colors break-words' }, p.prompt_text || 'Manual Import')
              ),
              div({ class: 'card-actions justify-end mt-2' },
                button({ class: 'btn btn-primary btn-sm btn-ghost group-hover:btn-active' }, p.status === 'REJECTED' ? 'View / Restore →' : 'View Art →')
              )
            )
            ))
          ),
          loadingMore.bindAs(lm => lm
            ? div({ class: 'flex justify-center py-6' }, span({ class: 'loading loading-spinner loading-md text-primary' }))
            : div()
          ),
          sentinel
        )
      })
    )
  )
}
